import { useState, useEffect } from 'react';
import { CustomRSVPQuestion } from '@/components/CustomRSVPBuilder';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface RSVPResponse {
  id: string;
  event_id: string;
  guest_id: string;
  question_id: string;
  answer: string | string[];
  created_at: string;
}

export interface RSVPFormData {
  id: string;
  event_id: string;
  questions: CustomRSVPQuestion[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useCustomRSVP = (eventId?: string) => {
  const { user } = useAuth();
  const [rsvpForm, setRSVPForm] = useState<RSVPFormData | null>(null);
  const [responses, setResponses] = useState<RSVPResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRSVPForm = async () => {
    if (!eventId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { data: formData, error: formError } = await (supabase as any)
        .from('custom_rsvp_forms')
        .select('*')
        .eq('event_id', eventId)
        .single();

      if (formError && formError.code !== 'PGRST116') {
        throw formError;
      }

      setRSVPForm(formData as RSVPFormData);
    } catch (err) {
      console.error('Error loading RSVP form:', err);
      setError('Failed to load RSVP form');
    } finally {
      setLoading(false);
    }
  };

  const loadResponses = async () => {
    if (!eventId) return;
    
    try {
      const { data: responsesData, error: responsesError } = await (supabase as any)
        .from('custom_rsvp_responses')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (responsesError) throw responsesError;

      setResponses((responsesData as RSVPResponse[]) || []);
    } catch (err) {
      console.error('Error loading RSVP responses:', err);
    }
  };

  useEffect(() => {
    if (eventId) {
      loadRSVPForm();
      loadResponses();
    }
  }, [eventId]);

  // Set up real-time subscription for responses
  useEffect(() => {
    if (!eventId) return;

    const channel = supabase
      .channel(`rsvp_responses_${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'custom_rsvp_responses',
          filter: `event_id=eq.${eventId}`,
        },
        () => {
          loadResponses();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId]);

  const saveRSVPForm = async (questions: CustomRSVPQuestion[]) => {
    if (!eventId || !user) return;

    try {
      const formData = {
        event_id: eventId,
        questions: questions,
        is_active: true,
      };

      const { data, error } = await (supabase as any)
        .from('custom_rsvp_forms')
        .upsert(formData, { onConflict: 'event_id' })
        .select()
        .single();

      if (error) throw error;

      setRSVPForm(data as RSVPFormData);
      toast.success('RSVP form saved successfully');
      return data;
    } catch (err) {
      console.error('Error saving RSVP form:', err);
      toast.error('Failed to save RSVP form');
      throw err;
    }
  };

  const submitResponse = async (guestId: string, answers: Record<string, string | string[]>) => {
    if (!eventId || !rsvpForm) {
      console.error('Missing eventId or rsvpForm:', { eventId, rsvpForm });
      throw new Error('RSVP form not loaded');
    }

    try {
      // Filter out empty answers
      const validAnswers = Object.entries(answers).filter(([_, answer]) => {
        if (Array.isArray(answer)) {
          return answer.length > 0;
        }
        return answer !== '' && answer !== null && answer !== undefined;
      });

      if (validAnswers.length === 0) {
        console.log('No valid answers to submit');
        return;
      }

      const responses = validAnswers.map(([questionId, answer]) => ({
        event_id: eventId,
        guest_id: guestId,
        question_id: questionId,
        answer: JSON.stringify(Array.isArray(answer) ? answer : [answer]),
      }));

      const { error } = await (supabase as any)
        .from('custom_rsvp_responses')
        .insert(responses);

      if (error) throw error;

      toast.success('RSVP submitted successfully');
      loadResponses();
    } catch (err) {
      console.error('Error submitting RSVP:', err);
      toast.error('Failed to submit RSVP');
      throw err;
    }
  };

  const exportResponses = async (format: 'json' | 'csv' = 'json') => {
    if (!eventId || !rsvpForm) return;

    try {
      const { data: guests, error: guestsError } = await supabase
        .from('event_guests')
        .select('id, email, name')
        .eq('event_id', eventId);

      if (guestsError) throw guestsError;

      const guestMap = new Map(guests?.map(g => [g.id, g]) || []);
      
      if (format === 'json') {
        const exportData = {
          event_id: eventId,
          form: rsvpForm,
          responses: responses.map(response => ({
            ...response,
            guest: guestMap.get(response.guest_id),
          })),
          exported_at: new Date().toISOString(),
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
          type: 'application/json' 
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rsvp-responses-${eventId}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else if (format === 'csv') {
        // Group responses by guest
        const responsesByGuest = responses.reduce((acc, response) => {
          if (!acc[response.guest_id]) {
            acc[response.guest_id] = {};
          }
          acc[response.guest_id][response.question_id] = response.answer;
          return acc;
        }, {} as Record<string, Record<string, string | string[]>>);

        // Create CSV headers
        const headers = ['Guest ID', 'Guest Name', 'Guest Email'];
        rsvpForm.questions.forEach(q => headers.push(q.text));

        // Create CSV rows
        const rows = [headers];
        Object.entries(responsesByGuest).forEach(([guestId, guestResponses]) => {
          const guest = guestMap.get(guestId);
          const row = [
            guestId,
            guest?.name || 'Unknown',
            guest?.email || 'Unknown'
          ];
          
          rsvpForm.questions.forEach(q => {
            const answer = guestResponses[q.id];
            row.push(Array.isArray(answer) ? answer.join(', ') : (answer || ''));
          });
          
          rows.push(row);
        });

        const csvContent = rows.map(row => 
          row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rsvp-responses-${eventId}-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      toast.success(`RSVP responses exported as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Error exporting responses:', err);
      toast.error('Failed to export responses');
    }
  };

  const getResponseAnalytics = () => {
    if (!rsvpForm || responses.length === 0) return null;

    const analytics = rsvpForm.questions.map(question => {
      const questionResponses = responses.filter(r => r.question_id === question.id);
      
      if (['select', 'radio', 'checkbox', 'multiselect'].includes(question.type) && question.options) {
        // Count responses for each option
        const optionCounts = question.options.reduce((acc, option) => {
          acc[option] = 0;
          return acc;
        }, {} as Record<string, number>);

        questionResponses.forEach(response => {
          const answers = Array.isArray(response.answer) ? response.answer : [response.answer];
          answers.forEach(answer => {
            if (optionCounts.hasOwnProperty(answer)) {
              optionCounts[answer]++;
            }
          });
        });

        return {
          question,
          responseCount: questionResponses.length,
          optionCounts,
          type: 'categorical'
        };
      } else {
        // For text, number, etc.
        return {
          question,
          responseCount: questionResponses.length,
          responses: questionResponses.map(r => r.answer),
          type: 'open'
        };
      }
    });

    return {
      totalResponses: new Set(responses.map(r => r.guest_id)).size,
      totalQuestions: rsvpForm.questions.length,
      questionAnalytics: analytics,
    };
  };

  return {
    rsvpForm,
    responses,
    loading,
    error,
    saveRSVPForm,
    submitResponse,
    exportResponses,
    getResponseAnalytics,
    loadRSVPForm,
    loadResponses,
  };
};